import moment from "moment";

/**
 * Strips the TRX prefix and tab/metric suffix, returning the base dashboard path.
 *   "TRX:/sm-store/sales-and-operations:sales [sales]" → "sm-store/sales-and-operations"
 *   "TRX:/sm-store/sales-and-operations"               → "sm-store/sales-and-operations"
 *   "sm-store/sales-and-operations"                    → "sm-store/sales-and-operations"
 */
export const normalizeDashboardModule = (module) => {
    if (!module || !module.startsWith('TRX:')) return module;
    const withoutPrefix = module.slice(4);
    const withoutSlash = withoutPrefix.startsWith('/') ? withoutPrefix.slice(1) : withoutPrefix;
    const colonIdx = withoutSlash.indexOf(':');
    return colonIdx !== -1 ? withoutSlash.slice(0, colonIdx) : withoutSlash;
};

/**
 * Returns the tab/metric detail string for a TRX entry, or null otherwise.
 *   "TRX:/sm-store/sales-and-operations:sales [sales]" → "sales [sales]"
 *   "TRX:/sm-store/sales-and-operations"               → null
 *   "sm-store/sales-and-operations"                    → null
 */
export const getTrxDetail = (module) => {
    if (!module || !module.startsWith('TRX:')) return null;
    const withoutPrefix = module.slice(4);
    const withoutSlash = withoutPrefix.startsWith('/') ? withoutPrefix.slice(1) : withoutPrefix;
    const colonIdx = withoutSlash.indexOf(':');
    if (colonIdx === -1) return null;
    const detail = withoutSlash.slice(colonIdx + 1).trim();
    if (detail === 'undefined' || detail === '') return null;
    return detail;
};

/**
 * Merges {module, view_count} items from the most-active-module API,
 * summing counts across TRX variants of the same base dashboard path.
 */
export const normalizeMostActiveModuleData = (data = []) => {
    const merged = {};
    data.forEach(item => {
        const key = normalizeDashboardModule(item.module);
        if (!merged[key]) merged[key] = { module: key, view_count: 0 };
        merged[key].view_count += item.view_count;
    });
    return Object.values(merged);
};

/**
 * Groups tracking history results by date + user + normalized module.
 * Each group's visits array contains {datetime, originalModule, detail} objects.
 * includeUser=false collapses the user dimension (for single-user audit views).
 */
export const groupTrackingResults = (results = [], includeUser = true) => {
    const groups = {};
    results.forEach(item => {
        const date = moment(item.datetime).format('YYYY-MM-DD');
        const normalizedModule = normalizeDashboardModule(item.module);
        const key = includeUser
            ? `${date}__${item.user}__${normalizedModule}`
            : `${date}__${normalizedModule}`;
        if (!groups[key]) {
            groups[key] = {
                id: key,
                date,
                user: item.user,
                module: normalizedModule,
                visits: [],
            };
        }
        groups[key].visits.push({
            datetime: item.datetime,
            originalModule: item.module,
            detail: getTrxDetail(item.module),
        });
    });
    return Object.values(groups).sort((a, b) => {
        if (b.date !== a.date) return b.date.localeCompare(a.date);
        if (includeUser && a.user !== b.user) return a.user.localeCompare(b.user);
        return a.module.localeCompare(b.module);
    });
};

